import {
    ProcessStage,
    ProcessStageArrow,
    ProcessStageArrowDirection,
    ProcessStageOrder,
    useProcessController
} from "../../Processes";
import * as React from "react";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {ValidatedElement} from "../models";
import {Display} from "../../../components/Display";
import {Button, Notification} from "@vebox/etui-core";
import {LoadingButton} from "../../../components/buttons/LoadingButton";
import {defaultTheme} from "@vebox/etui-themes";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";

declare var AppTranslations: AppTranslations;

type DeleteStageProps = {
    title: string,
    list: ValidatedElement[],
    action(validElements: ValidatedElement[]): Promise<void>
}

export function DeleteStage({title, list, action}: DeleteStageProps) {

    const [validated, setValidated] = React.useState<ValidatedElement[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);

    const {goTo} = useProcessController();

    function removeElement(code: string) {
        setValidated((prev) => {
            return prev.filter((v) => v.code != code);
        });
    }

    function removeNotValid() {
        setValidated((prev) => {
            return prev.filter((v) => v.valid);
        });
    }

    async function submit() {
        setLoading(true);
        try {
            await action(validated);
            Notification.open({
                type: "success",
                message: AppTranslations.entityDeleteProcess.successFullDeletionTitle,
                description: "",
                duration: 3,
                theme: defaultTheme,
            });
        } catch (e) {
            Notification.open({
                type: "error",
                message: AppTranslations.entityDeleteProcess.failedDeletionTitle,
                description: "",
                duration: 3,
                theme: defaultTheme,
            });
        } finally {
            goTo(0);
            setLoading(false);
        }
    }


    React.useEffect(() => {
        setValidated(list ?? [])
    }, [list]);


    React.useEffect(() => {
        if (validated.length === 0 && list?.length > 0) {
            goTo(0);
        }
    }, [validated]);

    const notValidCount = validated.reduce((sum, v) => sum +  (v.valid ? 0 : 1), 0);

    const canDelete = validated.reduce((r, v) => r && v.valid && !v.isDefaultWarehouse, true)


    return (
        <ProcessStage
            stepNumber={2}
            title={AppTranslations.genericWords.delete}
            stageOrder={ProcessStageOrder.LAST}
        >
            <ProcessStageArrow stepNumber={2} direction={ProcessStageArrowDirection.BACKWARD} visible={true}/>
            <div className={"flx-row"}>
                <div className={"codes-check-and-approve-box"}>
                    <VeBoxCard className={"form-card"} style={{maxHeight: "400px", gridRowStart: 2}}>
                        <ul className={"claim-list"}>
                            {validated
                                .sort(e => !e.valid ? -1 : 1)
                                .map((v) => {
                                    return (
                                        <ValidatedElementListItem validatedElement={v} onRemove={removeElement}/>
                                    );
                                })}
                        </ul>
                        <div className={"flx-row flx-space-between"}>
                            <p>
                                <Display isVisible={validated?.length > 0}>
                                    {validated.length - notValidCount} {validated.length - notValidCount === 1 ? AppTranslations.genericWords.found : AppTranslations.genericWords.foundPlural} {notValidCount} {notValidCount === 1 ? AppTranslations.genericWords.notFound : AppTranslations.genericWords.notFoundPlural}
                                </Display>
                            </p>
                            <div className={"flx-row flx-space-between"}>
                                <Button disabled={canDelete} variant={"tertiary"} onClick={removeNotValid}>
                                    {AppTranslations.entityDeleteProcess.removeNotValid}
                                </Button>
                                <LoadingButton
                                    variant={"strong"}
                                    onClick={submit}
                                    loading={loading}
                                    disabled={!canDelete}
                                    styles={{text: {textTransform: "capitalize"}}}
                                >
                                    {AppTranslations.genericWords.delete}
                                </LoadingButton>
                            </div>
                        </div>
                    </VeBoxCard>
                </div>
            </div>
        </ProcessStage>
    );
}

type ValidatedElementListItemProps = {
    validatedElement: ValidatedElement,
    onRemove(guid: string): void,
};

function ValidatedElementListItem({validatedElement, onRemove}: ValidatedElementListItemProps) {
    const remove = () => onRemove(validatedElement.code);

    return (
        <li className={`equipment-validated ${!validatedElement?.valid ? 'not-found' : ''}`}>
            <div className={"icon-section"}>
                <span className="material-icons all-self-center">
                    {validatedElement.valid || validatedElement.isDefaultWarehouse? "verified_user" : "priority_high"}
                </span>
            </div>
            <div className={"equipment-section"}>
                <h2 className={"device-info text-overflow-ellipsis-hidden align-self-center bold"}>{validatedElement?.code}</h2>
                {!validatedElement.valid && (
                    <small className={"device-info text-overflow-ellipsis-hidden align-self-center"}>
                        {AppTranslations.genericWords.notFound}
                    </small>
                )}
                {validatedElement.hasTelemetry && (
                    <small className={"device-info text-overflow-ellipsis-hidden align-self-center"}>
                        {AppTranslations.entityDeleteProcess.hasTelemetry}
                    </small>
                )}
                <small className={"device-info text-overflow-ellipsis-hidden align-self-center"}>
                    {validatedElement.isDefaultWarehouse && "Default warehouse"}
                    { validatedElement.isDefaultWarehouse && validatedElement.equipmentCount > 0 && " - "}
                    {validatedElement.equipmentCount > 0 && `${validatedElement.equipmentCount} equipment`}
                </small>
            </div>
            <Button onClick={remove} variant={"tertiary"}>
                <span className="material-symbols-outlined">delete</span>
            </Button>
        </li>
    );
}